import reactUpdate from 'immutability-helper';
import moment, {Moment} from 'moment';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {assert, assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NotebookSectionColor} from 'graphql-client/__generated/graphqlGlobalTypes';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {EntryNote, NoteDay} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';
import uniqueId from 'util/uniqueId';

export function makeDay(
  dateString: string,
  titleString: string | undefined,
  notePartIndex: number = 0,
  provenanceUuid?: string,
  sectionColor?: NotebookSectionColor | null
): NoteDay & {noteVersionCid: string} {
  return {
    date: dateString,
    index: notePartIndex,
    title: titleString,
    cid: uniqueId('entryDay'),
    provenanceUuid: provenanceUuid ?? uuidv4(),
    sectionColor: sectionColor ?? NotebookSectionColor.DEFAULT,
    noteVersionCid: uniqueId('note-version'),
  };
}

export function fillEmptyDays(note: EntryNote): EntryNote {
  // Finds the first occurrence of an empty day, updates the note parts accordingly, then
  // recurses.
  for (let i = 0, end = note.days.length, asc = 0 <= end; asc ? i < end : i > end; asc ? i++ : i--) {
    let day = note.days[i];
    const nextDay = note.days[i + 1];
    if (nextDay != null && day.index !== nextDay.index) {
      continue;
    }
    if (nextDay == null && day.index !== note.noteParts.length) {
      continue;
    }

    // Fill in note parts in between days with the same index, and days at the end with no noteParts.
    const {index} = day;
    const newNoteParts = reactUpdate(note.noteParts, {$splice: [[index, 0, makeDefaultItem()]]});
    const newDays = note.days.slice(0, i + 1);
    for (day of Array.from(note.days.slice(i + 1))) {
      newDays.push(
        reactUpdate(day, {
          index: {
            $apply(x: number) {
              return x + 1;
            },
          },
        })
      );
    }
    note = reactUpdate(note, {$merge: {noteParts: newNoteParts, days: newDays}});
    return fillEmptyDays(note);
  }
  return note;
}

export function formatDate(momentDate: Moment): string {
  return momentDate.format('YYYY-MM-DD');
}

/**
 * Returns the index within the noteDays array that we should insert a new day in given the notePartIndex.
 */
function noteDaySortedIndex<T extends {index: number}>(noteDays: Array<T>, notePartIndex?: number): number {
  return _.sortedIndex(noteDays, {index: notePartIndex}, (day) => day.index);
}

export function insertDay(
  note: EntryNote,
  notePartIndex: number,
  fileType: string | null,
  getTextForDate: (date: string) => string,
  title: string
): EntryNote {
  let newMomentDate;
  if (fileType == null) {
    fileType = ModelTypeConstants.ENTRY;
  }
  assert(notePartIndex > 0, "insertDay doesn't support inserting at beginning yet");
  const dayIndex = noteDaySortedIndex(note.days, notePartIndex);

  // We can't insert at the beginning, so there should always be a previousDay.
  const previousDay = note.days[dayIndex - 1];
  const nextDay = note.days[dayIndex];

  const previousMomentDate = moment(previousDay.date);
  // Not sure if there's a better way to get this, don't want to deal with timezones.
  const currentMomentDate = moment().startOf('day');

  // Try to insert today if possible, otherwise insert previous day + 1.
  if (
    previousMomentDate.isBefore(currentMomentDate) &&
    !APIUtil.isEntryOrSubOrWorkflowTemplateFileType(fileType) &&
    (nextDay == null || currentMomentDate.isBefore(nextDay.date))
  ) {
    newMomentDate = moment();
  } else {
    newMomentDate = previousMomentDate.clone().add(1, 'day');
  }

  if (nextDay != null && !newMomentDate.isBefore(nextDay.date)) {
    notifications.warn(
      `Date cannot be inserted between ${getTextForDate(previousDay.date)} and ${getTextForDate(
        nextDay.date
      )}`
    );
    return note;
  }

  const newDate = formatDate(newMomentDate);
  const newDay = makeDay(newDate, title, notePartIndex);
  const newDays = reactUpdate(note.days, {$splice: [[dayIndex, 0, newDay]]});
  note = reactUpdate(note, {$merge: {days: newDays}});
  note = fillEmptyDays(note);
  return note;
}

export function getPreviousAndNextDateFromIndex({
  note,
  dayIndex,
  notePartIndex,
  nextDayOffset = 0,
}: {
  note: EntryNote;
  dayIndex?: number;
  notePartIndex?: number;
  nextDayOffset?: number;
}): {previousDate: string; nextDate: string} {
  const dayIndexToUse = dayIndex != null ? dayIndex : noteDaySortedIndex(note.days, notePartIndex);

  let previousDate = '';
  let idx = dayIndexToUse - 1;
  while (idx >= 0) {
    previousDate = note.days[idx]?.date;
    if (previousDate) {
      break;
    }
    idx -= 1;
  }

  let nextDate = '';
  idx = dayIndexToUse + nextDayOffset;
  while (idx < note.days.length) {
    nextDate = note.days[idx]?.date;
    if (nextDate) {
      break;
    }
    idx += 1;
  }

  return {previousDate, nextDate};
}

function _checkDateIsBetween(
  dateAsMoment: Moment | null,
  startDate: string,
  endDate: string,
  getTextForDate: (date: string) => string,
  inclusive: boolean = false
): string {
  if (!dateAsMoment) {
    return '';
  }
  if (inclusive) {
    if (startDate && dateAsMoment.isBefore(startDate, 'day')) {
      return `Date cannot be inserted before ${getTextForDate(startDate)}`;
    }
    if (endDate && dateAsMoment.isAfter(endDate, 'day')) {
      return `Date cannot be inserted after ${getTextForDate(endDate)}`;
    }
  } else {
    if (startDate && dateAsMoment.isSameOrBefore(startDate, 'day')) {
      return `Date cannot be inserted before ${getTextForDate(startDate)}`;
    }
    if (endDate && dateAsMoment.isSameOrAfter(endDate, 'day')) {
      return `Date cannot be inserted after ${getTextForDate(endDate)}`;
    }
  }
  return '';
}

export function insertGivenDay(
  note: EntryNote,
  notePartIndex: number,
  fileType: string | null,
  getTextForDate: (date: string) => string,
  dateString: string,
  title?: string
): EntryNote {
  if (fileType == null) {
    fileType = ModelTypeConstants.ENTRY;
  }

  const dayIndex = noteDaySortedIndex(note.days, notePartIndex);
  const {previousDate, nextDate} = getPreviousAndNextDateFromIndex({note, dayIndex});

  if (!dateString && !title) {
    notifications.warn('Must have a date or title');
    return note;
  }

  const newMomentDate = dateString ? moment(dateString) : null;
  const dateError = _checkDateIsBetween(newMomentDate, previousDate, nextDate, getTextForDate, true);
  if (dateError) {
    notifications.warn(dateError);
    return note;
  }

  const newDate = newMomentDate ? formatDate(newMomentDate) : '';
  const newDay = makeDay(newDate, title, notePartIndex);
  const newDays = reactUpdate(note.days, {$splice: [[dayIndex, 0, newDay]]});
  note = reactUpdate(note, {$merge: {days: newDays}});
  note = fillEmptyDays(note);
  return note;
}

export function deleteDay(note: EntryNote, cid: string): EntryNote {
  // eslint-disable-next-line no-restricted-syntax
  const dayIndex = _.findIndex(note.days, {cid});
  if (note.days[dayIndex].index === 0) {
    assertAndContinue(false, "Can't delete first entry day");
    return note;
  } else {
    return reactUpdate(note, {days: {$splice: [[dayIndex, 1]]}});
  }
}

export function updateTitle(note: EntryNote, cid: string, newTitle: string, fileType: string): EntryNote {
  if (fileType == null) {
    fileType = ModelTypeConstants.ENTRY;
  }
  // eslint-disable-next-line no-restricted-syntax
  const dayIndex = _.findIndex(note.days, {cid});
  const day = note.days[dayIndex];

  if (!day.date && !newTitle) {
    notifications.warn('Must have a date or title');
    return note;
  }

  const updatedDay = reactUpdate(day, {$merge: {title: newTitle}});
  const updatedDays = reactUpdate(note.days, {$splice: [[dayIndex, 1, updatedDay]]});
  note = reactUpdate(note, {$merge: {days: updatedDays}});

  return note;
}

export function updateColor(note: EntryNote, cid: string, newColor: NotebookSectionColor): EntryNote {
  const dayIndex = note.days.findIndex((day) => day.cid === cid);
  const day = note.days[dayIndex];

  if (day.sectionColor === newColor) {
    return note;
  }

  const updatedDay = reactUpdate(day, {$merge: {sectionColor: newColor}});
  const updatedDays = reactUpdate(note.days, {$splice: [[dayIndex, 1, updatedDay]]});
  note = reactUpdate(note, {$merge: {days: updatedDays}});

  return note;
}

export function updateDay(
  note: EntryNote,
  cid: string,
  newTitle: string,
  newDate: string,
  getTextForDate: (date: string) => string,
  fileType?: string | null,
  requireDate: boolean = true
): EntryNote {
  if (fileType == null) {
    fileType = ModelTypeConstants.ENTRY;
  }
  // eslint-disable-next-line no-restricted-syntax
  const dayIndex = _.findIndex(note.days, {cid});
  const day = note.days[dayIndex];
  if (requireDate && !newDate) {
    newDate = day.date;
  }
  if (day.date === newDate && day.title === newTitle) {
    return note;
  }

  const newMomentDate = newDate ? moment(newDate) : null;
  let previousDate = note.days[dayIndex - 1]?.date;
  let nextDate = note.days[dayIndex + 1]?.date;
  let checkDateBoundsInclusive = false;
  if (!requireDate) {
    checkDateBoundsInclusive = true;
    ({previousDate, nextDate} = getPreviousAndNextDateFromIndex({note, dayIndex, nextDayOffset: 1}));

    if (!newDate && !newTitle) {
      notifications.warn('Must have a date or title');
      return note;
    }
  }
  const dateError = _checkDateIsBetween(
    newMomentDate,
    previousDate,
    nextDate,
    getTextForDate,
    checkDateBoundsInclusive
  );
  if (dateError) {
    notifications.warn(dateError);
    return note;
  }

  const newDay = reactUpdate(day, {$merge: {date: newDate, title: newTitle}});
  const newDays = reactUpdate(note.days, {$splice: [[dayIndex, 1, newDay]]});
  note = reactUpdate(note, {$merge: {days: newDays}});
  return note;
}

/**
 * Returns the updated note-part index for a single entry day after a replace/insert/delete.
 *
 * @param insertAtBoundaryStartsNextDay - When true, inserting at an entry-day boundary
 *   (`start === end === dayIndex`) keeps the day start at that index so new note parts belong
 *   to that day. When false or omitted, boundary inserts default to the previous day by
 *   pushing the day start forward (e.g. inserting after the last note part of a day).
 */
function adjustDayIndexForReplace(
  dayIndex: number,
  start: number,
  end: number,
  insertItemCount: number,
  options: {insertAtBoundaryStartsNextDay?: boolean} = {}
): number {
  // New end index after inserting the items
  const newEnd = start + insertItemCount;

  if (start < dayIndex && dayIndex <= end) {
    return newEnd;
  }

  const sectionAfterReplaceRange = dayIndex > end;
  const rangeAtBeginningOfSection = start === end && end === dayIndex;
  const sectionIsNotFirstSection = dayIndex !== 0;

  // In some cases, we want the index of our note-part to occur after a section boundary. Those cases are
  // when:
  // - our range ends after the section
  // - we are inserting at the beginning of a section and have explicitly NOT passed the option
  //   `insertAtBoundaryStartsNextDay`
  // For all other cases, we don't adjust our index
  if (
    sectionAfterReplaceRange ||
    (rangeAtBeginningOfSection && sectionIsNotFirstSection && !options.insertAtBoundaryStartsNextDay)
  ) {
    // Net shift in indices for all the sections after the range being replaced
    const indexShift = newEnd - end;
    return dayIndex + indexShift;
  }

  // If section is before the range being replaced, no change
  return dayIndex;
}

/**
 * Updates entry-day start indices after note parts are replaced, inserted, or deleted.
 *
 * @param insertAtBoundaryStartsNextDay - When true, inserting at an entry-day boundary keeps
 *   the day start at the insertion index so new note parts belong to that day. When false or
 *   omitted, boundary inserts default to the previous day by pushing the day start forward.
 */
export function updateDaysForReplace(
  note: EntryNote,
  start: number,
  end: number,
  insertItemCount: number,
  options: {fillEmptyDays?: boolean; insertAtBoundaryStartsNextDay?: boolean} = {}
): EntryNote {
  const newDays = note.days.map((day) => {
    const newIndex = adjustDayIndexForReplace(day.index, start, end, insertItemCount, options);
    return newIndex === day.index ? day : {...day, index: newIndex};
  });

  note = reactUpdate(note, {$merge: {days: newDays}});
  if (options?.fillEmptyDays === false) {
    return note;
  }
  return fillEmptyDays(note);
}
